import React, { useEffect, useState } from "react";
import { Shield, Toggle, AlertCircle, Sparkles, Mic, Eye, Activity, FlaskConical, FileSignature, Lock } from "lucide-react";
import { Page, Card, Pill, PrimaryButton } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

const GLOBAL_TOGGLES = [
  { key: "master_ai_enabled", label: "Master AI Switch", desc: "Master kill switch — when OFF, NO AI features run anywhere in the platform.", icon: Sparkles, danger: true },
  { key: "ai_notes_enabled", label: "AI Notes Generation", desc: "Allow AI to generate progress notes (per-client toggle still applies).", icon: Sparkles },
  { key: "microphone_enabled", label: "Microphone Access", desc: "Master control for ALL microphone access. When OFF, recording and transcription are disabled platform-wide.", icon: Mic },
  { key: "ai_billing_suggestions", label: "AI Billing Suggestions", desc: "AI suggests CPT/ICD-10 codes and flags billing errors. Static rules remain when OFF.", icon: Activity },
  { key: "ai_intake_scoring", label: "AI Intake Scoring", desc: "AI auto-scores PHQ-9, GAD-7, PCL-5 and generates clinical summaries.", icon: Activity },
  { key: "crisis_monitoring_enabled", label: "Predictive Crisis Monitoring", desc: "Master enable for passive AI crisis detection. Defaults OFF — practice policy required.", icon: AlertCircle, danger: true },
  { key: "ai_voice_learning", label: "AI Voice Learning", desc: "AI learns your writing style after 10 accepted notes. When OFF, generic templates only.", icon: Eye },
];

export default function Privacy() {
  const [global, setGlobal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/privacy/global").then(({ data }) => setGlobal(data));
  }, []);

  const toggle = async (key, value) => {
    setSaving(true);
    const newGlobal = { ...global, [key]: value };
    setGlobal(newGlobal);
    try {
      await api.put("/privacy/global", { [key]: value });
      toast.success("Privacy setting updated");
    } catch {
      toast.error("Save failed");
      setGlobal(global);
    }
    setSaving(false);
  };

  if (!global) return <Page title="Privacy & AI Controls"><div className="mf-skeleton h-48"></div></Page>;

  return (
    <Page title="Privacy & AI Controls" subtitle="Practice-wide control over every AI feature. Privacy is a first-class right.">
      <Card delay={0.05} className="mb-5" testid="privacy-banner">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(26,107,114,0.12)" }}>
            <Shield className="w-5 h-5" style={{ color: "var(--mf-primary)" }} />
          </div>
          <div className="flex-1">
            <div className="font-serif text-xl mb-1">Your data, your control</div>
            <div className="text-sm" style={{ color: "var(--mf-text-muted)" }}>These settings apply platform-wide. Use the per-client privacy panel (shield icon on each client) for granular controls.</div>
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        {GLOBAL_TOGGLES.map((t, i) => (
          <Card key={t.key} delay={0.1 + i*0.04} testid={`toggle-${t.key}`}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: t.danger ? "rgba(180,82,83,0.12)" : "rgba(26,107,114,0.1)" }}>
                <t.icon className="w-5 h-5" style={{ color: t.danger ? "#B45253" : "var(--mf-primary)" }} strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-medium">{t.label}</div>
                  {t.danger && <Pill variant="warn">Practice policy required</Pill>}
                </div>
                <div className="text-sm" style={{ color: "var(--mf-text-muted)" }}>{t.desc}</div>
              </div>
              <button
                onClick={() => toggle(t.key, !global[t.key])}
                disabled={saving}
                className="w-14 h-8 rounded-full relative transition-colors shrink-0"
                style={{ background: global[t.key] ? "var(--mf-primary)" : "var(--mf-muted)" }}
                data-testid={`switch-${t.key}`}
                aria-label={t.label}
              >
                <span className="absolute top-1 w-6 h-6 rounded-full bg-white transition-transform" style={{ left: global[t.key] ? "calc(100% - 1.75rem)" : "0.25rem" }}></span>
              </button>
            </div>
          </Card>
        ))}
      </div>

      <Card delay={0.5} className="mt-6" testid="privacy-info">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 mt-0.5" style={{ color: "var(--mf-accent)" }} />
          <div className="text-sm" style={{ color: "var(--mf-text-muted)" }}>
            <strong style={{ color: "var(--mf-text)" }}>What Clinician Well never does:</strong> sell your data, train external AI models on PHI, share with non-treating providers without consent. All AI requests are processed under our HIPAA BAA with the AI provider.
          </div>
        </div>
      </Card>
    </Page>
  );
}
